/**
 * Routine の締め(Gate・2026-10-23)。Routine(運送)で締めを確定すると届く「確定の写し」を、月ごと・段ごとに見る(表示だけ)。
 *  - ①請求(元請けごと)→ 売上として記録 → 請求書の画面の「未請求の売上」→ 経理が発行
 *  - ②下請けの支払い(会社ごと)・③ドライバーの支払い(人ごと)→ 支払キュー・承認・支払い待ちに入る
 *  - 各行に Gate での状態(請求書の発行・支払いの進み具合)を出し、明細は Routine の PDF と同じ形で開く
 *  - 見られる人: 経理・承認者・管理者(支払キュー・請求書と同じ)
 * 書き込みはしない。受け取り(受け口)は Routine 側の改修とあわせて作る(スイッチ OFF)。今はテスト環境のサンプル
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@core/components/Toast';
import { formatYen } from '@core/lib/money';
import { todayStr, toShortDate } from '@core/lib/date';
import type { Invoice, MonthStr, RoutineClosing, RoutineClosingStage, RoutineDriverDetail, RoutineSubcontractDetail, Transaction } from '@core/lib/types';
import {
  STAGE_LABELS,
  STAGE_ORDER,
  billingColumnCount,
  gateStateOf,
  invoiceByTransaction,
  monthLabel,
  shortDateTime,
  summarizeClosings,
  type GateState,
} from './routineClosing';
import { fetchInvoicesOfMonth, fetchRoutineClosings, fetchTransactionsByIds } from './routineClosingRepo';
import RoutineClosingDetailModal, { GateStateBadge } from './RoutineClosingDetailModal';

/** 今月を含む直近6か月(新しい順) */
function recentMonths(today: string): MonthStr[] {
  const [y, m] = today.split('-').map(Number) as [number, number];
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

type Loaded = { closings: RoutineClosing[]; txById: Map<string, Transaction>; invoices: Invoice[] };

export default function RoutineClosingsPage() {
  const toast = useToast();
  const months = useMemo(() => recentMonths(todayStr()), []);
  const [all, setAll] = useState<RoutineClosing[] | null>(null);
  const [month, setMonth] = useState<MonthStr | null>(null);
  const [stage, setStage] = useState<RoutineClosingStage>('billing');
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<RoutineClosing | null>(null);

  // 直近6か月の確定を読み、確定のある一番新しい月を開く(無ければ前月)
  useEffect(() => {
    let alive = true;
    fetchRoutineClosings(months)
      .then((list) => {
        if (!alive) return;
        setAll(list);
        const withData = months.find((m) => list.some((c) => c.month === m));
        setMonth((cur) => cur ?? withData ?? months[1] ?? months[0]!);
      })
      .catch((e) => {
        if (!alive) return;
        toast(e instanceof Error ? e.message : 'Routine の締めを読み込めませんでした');
        setAll([]);
        setMonth(months[1] ?? months[0]!);
      });
    return () => {
      alive = false;
    };
  }, [months, toast]);

  const load = useCallback(async () => {
    if (!all || !month) return;
    setLoading(true);
    try {
      const closings = all.filter((c) => c.month === month);
      const [txs, invoices] = await Promise.all([
        fetchTransactionsByIds(closings.flatMap((c) => c.transaction_ids)),
        fetchInvoicesOfMonth(month).catch(() => [] as Invoice[]),
      ]);
      setLoaded({ closings, txById: new Map(txs.map((t) => [t.id, t])), invoices });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gate の状態を読み込めませんでした');
      setLoaded(null);
    } finally {
      setLoading(false);
    }
  }, [all, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const invoiceByTx = useMemo(() => invoiceByTransaction(loaded?.invoices ?? []), [loaded]);
  const stateOf = useCallback(
    (c: RoutineClosing): GateState => gateStateOf(c, loaded?.txById ?? new Map(), invoiceByTx),
    [loaded, invoiceByTx],
  );
  const summary = useMemo(() => summarizeClosings(loaded?.closings ?? []), [loaded]);
  const rows = useMemo(
    () => (loaded?.closings ?? []).filter((c) => c.stage === stage).sort((a, b) => a.id.localeCompare(b.id)),
    [loaded, stage],
  );
  const monthsWithData = new Set((all ?? []).map((c) => c.month));

  return (
    <section className="screen">
      <div className="card">
        <h3>Routine の締め(運送)</h3>
        <p className="hint" style={{ margin: '0 0 10px' }}>
          Routine で締めを確定すると、ここに届きます。①請求は売上として記録され、請求書の画面の「未請求の売上」から経理が発行します。
          ②③の支払いは支払キュー・承認・支払い待ちに入ります。いまはテスト用のサンプルです(Routine とはまだつながっていません)。
        </p>

        <div className="acct-toolbar">
          <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={month ?? ''} onChange={(e) => setMonth(e.target.value)} aria-label="締めの月">
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}分{monthsWithData.has(m) ? '' : '(確定なし)'}
                </option>
              ))}
            </select>
          </span>
          <span className="chips" style={{ margin: 0 }}>
            {STAGE_ORDER.filter((s) => s !== 'fuel').map((s) => (
              <button key={s} className={`chip${stage === s ? ' on' : ''}`} onClick={() => setStage(s)}>
                {STAGE_LABELS[s]} {summary[s].count}件
              </button>
            ))}
          </span>
        </div>

        {loading || !loaded ? (
          <p className="empty">{loading ? '読み込み中…' : '表示できるデータがありません'}</p>
        ) : rows.length === 0 ? (
          <p className="empty">{month ? `${monthLabel(month)}分の${STAGE_LABELS[stage]}の確定はまだありません` : ''}</p>
        ) : (
          <ClosingTable stage={stage} rows={rows} stateOf={stateOf} onOpen={setDetail} />
        )}
      </div>
      {detail && <RoutineClosingDetailModal closing={detail} state={stateOf(detail)} onClose={() => setDetail(null)} />}
    </section>
  );
}

function StateCell({ state }: { state: GateState }) {
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <GateStateBadge state={state} />
      {state.to && (
        <Link to={state.to} className="txn-link">
          {state.linkLabel}
        </Link>
      )}
    </span>
  );
}

function ClosingTable({
  stage,
  rows,
  stateOf,
  onOpen,
}: {
  stage: RoutineClosingStage;
  rows: RoutineClosing[];
  stateOf: (c: RoutineClosing) => GateState;
  onOpen: (c: RoutineClosing) => void;
}) {
  const total = rows.filter((c) => c.status !== 'cancelled').reduce((a, c) => a + c.total, 0);
  const open = (c: RoutineClosing) => (
    <button className="chip" onClick={() => onOpen(c)}>
      明細
    </button>
  );
  const confirmed = (c: RoutineClosing) => <span style={{ whiteSpace: 'nowrap' }}>{shortDateTime(c.confirmed_at)}</span>;

  if (stage === 'billing') {
    return (
      <table>
        <thead>
          <tr>
            <th>元請け</th>
            <th className="num">拠点</th>
            <th className="num">稼働人数</th>
            <th className="num">請求額(税込)</th>
            <th>確定日時</th>
            <th>Gate での状態</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td className="num">{c.detail.kind === 'billing' ? c.detail.locations.length : '—'}</td>
              <td className="num">{c.detail.kind === 'billing' ? billingColumnCount(c.detail) : '—'}</td>
              <td className="num">{formatYen(c.total)}</td>
              <td>{confirmed(c)}</td>
              <td>
                <StateCell state={stateOf(c)} />
              </td>
              <td>{open(c)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>合計({rows.length}社)</td>
            <td />
            <td />
            <td className="num">{formatYen(total)}</td>
            <td />
            <td />
            <td />
          </tr>
        </tbody>
      </table>
    );
  }

  if (stage === 'subcontract') {
    return (
      <table>
        <thead>
          <tr>
            <th>下請け会社</th>
            <th className="num">人数</th>
            <th className="num">支払額</th>
            <th>支払い期日</th>
            <th>確定日時</th>
            <th>Gate での状態</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const d = c.detail as RoutineSubcontractDetail;
            return (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td className="num">{d.people?.length ?? 0}</td>
                <td className="num">{formatYen(c.total)}</td>
                <td>{d.due_date ? toShortDate(d.due_date) : '—'}</td>
                <td>{confirmed(c)}</td>
                <td>
                  <StateCell state={stateOf(c)} />
                </td>
                <td>{open(c)}</td>
              </tr>
            );
          })}
          <tr className="total">
            <td>合計({rows.length}社)</td>
            <td />
            <td className="num">{formatYen(total)}</td>
            <td />
            <td />
            <td />
            <td />
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          <th>ドライバー</th>
          <th className="num">稼働日数</th>
          <th className="num">報酬計</th>
          <th className="num">控除計</th>
          <th className="num">差引支払額</th>
          <th>確定日時</th>
          <th>Gate での状態</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => {
          const d = c.detail as RoutineDriverDetail;
          return (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td className="num">{d.days}</td>
              <td className="num">{formatYen(d.reward_total)}</td>
              <td className="num">{formatYen(d.ded_total)}</td>
              <td className="num">{formatYen(c.total)}</td>
              <td>{confirmed(c)}</td>
              <td>
                <StateCell state={stateOf(c)} />
              </td>
              <td>{open(c)}</td>
            </tr>
          );
        })}
        <tr className="total">
          <td>合計({rows.length}人)</td>
          <td />
          <td />
          <td />
          <td className="num">{formatYen(total)}</td>
          <td />
          <td />
          <td />
        </tr>
      </tbody>
    </table>
  );
}
